import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, User, Mail, Phone, MapPin, Eye, EyeOff, Save, Camera, X } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { FirestoreService } from '../../firebase/firestore';
import { AuthService } from '../../firebase/auth';
import { Customer } from '../../types';

const CustomerEditProfile: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [, setCustomerData] = useState<Customer | null>(null);
  const [dataLoading, setDataLoading] = useState(true);

  // Load user data on component mount
  useEffect(() => {
    const fetchCustomerData = async () => {
      console.log('🔍 CustomerEditProfile: Starting customer data fetch...');
      console.log('📊 CustomerEditProfile: Current user:', user);
      console.log('📊 CustomerEditProfile: User customerId:', user?.customerId);
      
      if (user?.id) {
        try {
          console.log('🔍 CustomerEditProfile: Fetching customer data for Firebase UID:', user.id);
          const customer = await FirestoreService.getCustomer(user.id);
          
          if (customer) {
            console.log('✅ CustomerEditProfile: Customer data loaded successfully:');
            console.log('📊 CustomerEditProfile: Customer data:', JSON.stringify(customer, null, 2));
            setCustomerData(customer);
            
            // Populate form with customer data
            setFormData({
              fullName: customer.name || user.name || '',
              email: customer.email || user.email || '',
              phone: customer.phone || '',
              address: customer.address || '',
              city: customer.city || '',
              currentPassword: '',
              newPassword: '',
              confirmPassword: ''
            });
            
            // Set profile picture
            setProfilePicture(customer.profilePicture || user.profilePicture || null);
            console.log('✅ CustomerEditProfile: Form data populated with customer data');
          } else {
            console.log('⚠️ CustomerEditProfile: No customer data found, using basic user data');
            // Fallback to basic user data
            setFormData({
              fullName: user.name || '',
              email: user.email || '',
              phone: '',
              address: '',
              city: '',
              currentPassword: '',
              newPassword: '',
              confirmPassword: ''
            });
          }
        } catch (error) {
          console.error('❌ CustomerEditProfile: Error fetching customer data:', error);
          // Fallback to basic user data
          setFormData({
            fullName: user.name || '',
            email: user.email || '',
            phone: '',
            address: '',
            city: '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
        }
      } else {
        console.log('⚠️ CustomerEditProfile: No user ID available, using basic user data');
        setFormData({
          fullName: user?.name || '',
          email: user?.email || '',
          phone: '',
          address: '',
          city: '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
      
      setDataLoading(false);
      console.log('✅ CustomerEditProfile: Data loading completed');
    };

    if (user) {
      fetchCustomerData();
    }
  }, [user]);

  const handleBack = () => {
    setCurrentScreen('customer-profile');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateField = (name: string, value: string) => {
    switch (name) {
      case 'fullName':
        return value.trim().length >= 2;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value);
      case 'phone':
        const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
        const cleanPhone = value.replace(/[\s\-()]/g, '');
        return phoneRegex.test(cleanPhone) && cleanPhone.length >= 10;
      case 'address':
        return value.trim().length >= 10;
      case 'city':
        return value.trim().length >= 2;
      default:
        return true;
    }
  };

  const validateForm = () => {
    // Clear previous errors
    setError('');
    
    if (!formData.fullName.trim()) {
      setError('Full name is required');
      return false;
    }
    if (formData.fullName.trim().length < 2) {
      setError('Full name must be at least 2 characters long');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    // Enhanced phone validation - accept various formats
    const phoneRegex = /^[+]?[1-9][\d]{0,15}$/;
    const cleanPhone = formData.phone.replace(/[\s\-()]/g, '');
    if (!phoneRegex.test(cleanPhone) || cleanPhone.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    if (!formData.address.trim()) {
      setError('Address is required');
      return false;
    }
    if (formData.address.trim().length < 10) {
      setError('Please provide a complete address');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }
    if (formData.city.trim().length < 2) {
      setError('Please enter a valid city name');
      return false;
    }
    
    // Password validation (only if user wants to change password)
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        setError('Current password is required to change password');
        return false;
      }
      if (formData.newPassword.length < 8) {
        setError('New password must be at least 8 characters long');
        return false;
      }
      // Check password strength
      const hasUpperCase = /[A-Z]/.test(formData.newPassword);
      const hasLowerCase = /[a-z]/.test(formData.newPassword);
      const hasNumbers = /\d/.test(formData.newPassword);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
        setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
        return false;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        setError('New passwords do not match');
        return false;
      }
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      console.log('💾 CustomerEditProfile: Starting profile update...');
      console.log('📊 CustomerEditProfile: Form data to save:', JSON.stringify(formData, null, 2));
      
      if (!user?.id) {
        throw new Error('No user ID available');
      }

      // Update user data in Firestore
      console.log('💾 CustomerEditProfile: Updating user data in Firestore...');
      const userUpdateData = {
        name: formData.fullName,
        email: formData.email,
        ...(profilePicture && { profilePicture })
      };
      
      const userUpdateSuccess = await FirestoreService.updateUser(user.id, userUpdateData);
      if (!userUpdateSuccess) {
        throw new Error('Failed to update user data');
      }
      console.log('✅ CustomerEditProfile: User data updated in Firestore');

      // Update customer data in Firestore
      console.log('💾 CustomerEditProfile: Updating customer data in Firestore...');
      const customerUpdateData = {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        ...(profilePicture && { profilePicture })
      };
      
      console.log('📊 CustomerEditProfile: Customer update data:', JSON.stringify(customerUpdateData, null, 2));
      console.log('📊 CustomerEditProfile: Phone:', customerUpdateData.phone);
      console.log('📊 CustomerEditProfile: Address:', customerUpdateData.address);
      console.log('📊 CustomerEditProfile: City:', customerUpdateData.city);
      
      const customerUpdateSuccess = await FirestoreService.updateCustomer(user.id, customerUpdateData);
      if (!customerUpdateSuccess) {
        throw new Error('Failed to update customer data');
      }
      console.log('✅ CustomerEditProfile: Customer data updated in Firestore');

      // Handle password change if provided
      if (formData.currentPassword && formData.newPassword) {
        console.log('🔐 CustomerEditProfile: Updating password...');
        const passwordResult = await AuthService.changePassword(formData.currentPassword, formData.newPassword);
        if (!passwordResult.success) {
          throw new Error(passwordResult.error || 'Failed to change password');
        }
        console.log('✅ CustomerEditProfile: Password updated successfully');
      }

      // Update current user session in localStorage
      const updatedUser = {
        ...user,
        name: formData.fullName,
        email: formData.email,
        ...(profilePicture && { profilePicture })
      };
      localStorage.setItem('bookfood_user', JSON.stringify(updatedUser));
      console.log('✅ CustomerEditProfile: User session updated in localStorage');

      setSuccess('Profile updated successfully!');
      console.log('🎉 CustomerEditProfile: Profile update completed successfully');
      
      // Navigate back to profile after a short delay
      setTimeout(() => {
        setCurrentScreen('customer-profile');
      }, 2000);

    } catch (err) {
      console.error('❌ CustomerEditProfile: Error updating profile:', err);
      setError(`Failed to update profile: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setCurrentScreen('customer-profile');
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicture(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveProfilePicture = () => {
    setProfilePicture(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const getPasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
    return Math.min(Math.floor(strength / 1.25), 4); // Scale to 1-4
  };

  const getPasswordStrengthText = (password: string): string => {
    const strength = getPasswordStrength(password);
    switch (strength) {
      case 1: return 'Very Weak';
      case 2: return 'Weak';
      case 3: return 'Good';
      case 4: return 'Strong';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between p-6 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center space-x-4">
          <motion.button
            onClick={handleBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft size={20} />
          </motion.button>
          <h1 className="text-xl font-bold text-white">Edit Profile</h1>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Loading State */}
        {dataLoading ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800 rounded-xl p-6 border border-slate-700"
          >
            <div className="text-center text-slate-400">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-2"></div>
              Loading profile data...
            </div>
          </motion.div>
        ) : (
          <>
        {/* Edit Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800 rounded-xl p-6 border border-slate-700"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Picture Section */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Profile Picture</h3>
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                    className="w-24 h-24 bg-slate-700 rounded-full flex items-center justify-center border-2 border-slate-600 cursor-pointer hover:border-teal-500 transition-colors duration-200"
                    onClick={handleProfilePictureClick}
                  >
                    {profilePicture ? (
                      <img
                        src={profilePicture}
                        alt="Profile"
                        className="w-24 h-24 rounded-full object-cover"
                      />
                    ) : (
                      <Camera className="text-slate-400" size={32} />
                    )}
                  </motion.div>
                  
                  {profilePicture && (
                    <motion.button
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={handleRemoveProfilePicture}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white transition-colors duration-200"
                    >
                      <X size={14} />
                    </motion.button>
                  )}
                </div>
                
                <div className="flex-1">
                  <p className="text-slate-300 text-sm mb-2">
                    {profilePicture ? 'Click to change profile picture' : 'Click to upload profile picture'}
                  </p>
                  <p className="text-slate-400 text-xs">
                    Recommended: Square image, max 5MB (JPG, PNG)
                  </p>
                </div>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
            </div>

            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${
                        formData.email && !validateField('email', formData.email)
                          ? 'border-red-500 focus:ring-red-500'
                          : formData.email && validateField('email', formData.email)
                          ? 'border-green-500 focus:ring-green-500'
                          : 'border-slate-600 focus:ring-teal-500'
                      }`}
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  {formData.email && !validateField('email', formData.email) && (
                    <p className="text-red-400 text-xs mt-1">Please enter a valid email address</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-3 bg-slate-700 border rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${
                        formData.phone && !validateField('phone', formData.phone)
                          ? 'border-red-500 focus:ring-red-500'
                          : formData.phone && validateField('phone', formData.phone)
                          ? 'border-green-500 focus:ring-green-500'
                          : 'border-slate-600 focus:ring-teal-500'
                      }`}
                      placeholder="Enter 10-digit phone number"
                      required
                    />
                  </div>
                  {formData.phone && !validateField('phone', formData.phone) && (
                    <p className="text-red-400 text-xs mt-1">Please enter a valid 10-digit phone number</p>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Address Information</h3>
              
              {/* Address */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Address *
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 text-slate-400" size={20} />
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                    placeholder="Enter address"
                    rows={3}
                    required
                  />
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  placeholder="City"
                  required
                />
              </div>
            </div>

            {/* Password Change Section */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Change Password (Optional)</h3>
              <div className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-12 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-12 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {formData.newPassword && (
                    <div className="mt-2">
                      <div className="flex space-x-1 mb-2">
                        {[1, 2, 3, 4].map((level) => {
                          const strength = getPasswordStrength(formData.newPassword);
                          return (
                            <div
                              key={level}
                              className={`h-1 flex-1 rounded ${
                                level <= strength
                                  ? strength === 1
                                    ? 'bg-red-500'
                                    : strength === 2
                                    ? 'bg-yellow-500'
                                    : strength === 3
                                    ? 'bg-blue-500'
                                    : 'bg-green-500'
                                  : 'bg-slate-600'
                              }`}
                            />
                          );
                        })}
                      </div>
                      <p className={`text-xs ${
                        getPasswordStrength(formData.newPassword) === 1 ? 'text-red-400' :
                        getPasswordStrength(formData.newPassword) === 2 ? 'text-yellow-400' :
                        getPasswordStrength(formData.newPassword) === 3 ? 'text-blue-400' :
                        getPasswordStrength(formData.newPassword) === 4 ? 'text-green-400' : 'text-slate-400'
                      }`}>
                        {getPasswordStrengthText(formData.newPassword)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-12 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Error and Success Messages */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-red-900/20 border border-red-500/50 rounded-lg p-4 text-red-400 text-sm"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span>{error}</span>
                </div>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-green-900/20 border border-green-500/50 rounded-lg p-4 text-green-400 text-sm"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>{success}</span>
                </div>
              </motion.div>
            )}

            {/* Form Buttons */}
            <div className="flex gap-4 pt-4">
              <motion.button
                type="button"
                onClick={handleCancel}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-slate-600 hover:bg-slate-500 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Cancel
              </motion.button>
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: isLoading ? 1 : 1.02, y: isLoading ? 0 : -2 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 disabled:from-slate-600 disabled:to-slate-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                {isLoading ? (
                  <>
                    <motion.div 
                      className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    ></motion.div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    <span>Save Changes</span>
                  </>
                )}
              </motion.button>
            </div>
          </form>
        </motion.div>
          </>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default CustomerEditProfile;
